import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismBreadcrumb,
  PrismButton,
  PrismLoadingBar,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import MarketSettingsCentralMappingBannerSaving from 'lumen/components/market/settings/central-mapping/banner-saving';
import MarketSettingsCentralMappingTablesTableMapping from 'lumen/components/market/settings/central-mapping/tables/table-mapping';
import SettingsCentralMappingDropdownsDropdownMappingMoreActions from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-more-actions';
import SettingsCentralMappingModalsModalMappingOnboarding from 'lumen/components/settings/central-mapping/modals/modal-mapping-onboarding';
import SettingsCentralMappingModalsModalUploadMapping from 'lumen/components/settings/central-mapping/modals/modal-upload-mapping';
import SettingsMappingModalsSegmentationViewModal from 'lumen/components/settings/mapping/modals/segmentation-view-modal';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type MarketSettingsCentralMappingMappingController from 'lumen/controllers/market/settings/central-mapping/mapping';
import type MarketSettingsCentralMappingMappingRoute from 'lumen/routes/market/settings/central-mapping/mapping';
import { DEFAULT_PAGE_SIZE } from 'lumen/utils/central-mapping-constants';
import type { ModelFrom } from 'lumen/utils/routes';

interface MarketSettingsCentralMappingMappingSignature {
  Args: {
    controller: MarketSettingsCentralMappingMappingController;
    model: ModelFrom<MarketSettingsCentralMappingMappingRoute>;
  };
}

<template>
  {{#if @controller.isPageLoading}}
    <PrismLoadingBar />
  {{/if}}

  <SettingsPageTemplate
    @badge={{if
      @controller.isReadOnly
      (t "settings.central_mapping.read_only.view_only")
    }}
    @isFullHeight={{true}}
    @title={{@controller.title}}
    class="central-mapping"
    data-test-id="central-mapping-mapping-page-template"
  >
    <:breadcrumb>
      <PrismBreadcrumb as |B|>
        <B.item
          @label={{t "settings.pms_mapping.mapping"}}
          @route="market.settings.central-mapping.menu"
        />
        <B.item @isCurrent={{true}} @label={{@controller.title}} />
      </PrismBreadcrumb>
    </:breadcrumb>

    <:headerAfter>
      <div class="flex flex-row gap-200">
        {{#unless @controller.isPageLoading}}
          {{#if @controller.isReadOnly}}
            <PrismButton
              @buttonType="ghost"
              @iconBefore="book"
              @label={{t "general.help_center"}}
              @onClick={{@controller.openHelpArticle}}
              data-test-id="central-mapping-mapping-help-center-link"
            />

            <PrismButton
              @buttonType="secondary"
              @label={{@controller.navigateToHierarchyButtonLabel}}
              data-test-id="central-mapping-mapping-navigate-to-hierarchy-button"
              {{on "click" @controller.onClickNavigateToHierarchy}}
            />
          {{else}}
            <SettingsCentralMappingDropdownsDropdownMappingMoreActions
              @dropdownOptions={{@controller.moreActionsOptions}}
              @onSelect={{@controller.onSelectMoreActionsOption}}
            />

            <PrismButton
              @buttonType="secondary"
              @isDisabled={{or
                @controller.isSaving
                (not @controller.hasChanges)
              }}
              @label={{t "settings.central_mapping.discard"}}
              @onClick={{@controller.onClickDiscardChanges}}
              data-test-id="central-mapping-mapping-discard-changes-button"
            />

            <PrismButton
              @isDisabled={{or
                @controller.updateMappingTask.isRunning
                (not @controller.hasChanges)
              }}
              @isLoading={{@controller.updateMappingTask.isRunning}}
              @label={{t "central_mapping.save_changes"}}
              data-test-id="central-mapping-mapping-save-changes-button"
              {{on "click" @controller.onClickSave}}
            />
          {{/if}}
        {{/unless}}

      </div>
    </:headerAfter>

    <:default>
      <div class="flex h-full flex-col gap-300">
        {{#if @controller.centralMappingSaving.isProcessing}}
          <MarketSettingsCentralMappingBannerSaving
            @isMappingTaskRunning={{@model.mapping.isRunning}}
            @refreshMappingEntries={{@model.refreshMappingEntries}}
          />
        {{/if}}

        <div class="min-h-0 flex-1">
          <MarketSettingsCentralMappingTablesTableMapping
            @appliedFilters={{@controller.appliedFilters}}
            @applyFilters={{@controller.applyMappingFilters}}
            @brandOptions={{@controller.brandOptions}}
            @breakdown={{@controller.breakdown}}
            @chainOptions={{@controller.chainOptions}}
            @curentPageSizeValueOption={{@controller.curentPageSizeValueOption}}
            @currentPage={{@controller.page}}
            @endIndexPage={{or @controller.mappingPaginationData.endIndex 0}}
            @filterOption={{@controller.filterOption}}
            @hasUnsavedChanges={{@controller.hasChanges}}
            @hierarchyCategories={{@controller.hierarchy}}
            @isLoading={{@model.mapping.isRunning}}
            @isLoadingMarketFilters={{@model.marketSubscriptions.isRunning}}
            @isMappingReadOnly={{@controller.isDisabled}}
            @isStaffUser={{@controller.isStaffUser}}
            @lastPage={{or @controller.mappingPaginationData.pageCount 0}}
            @mapping={{@controller.mapping}}
            @marketMappingFilters={{@controller.marketMappingFilters}}
            @marketSubscriptions={{@controller.marketSubscriptions}}
            @onChangePageSize={{@controller.onChangePageSize}}
            @onClickUploadMapping={{@controller.onClickUploadMapping}}
            @onRemoveFilterClicked={{@controller.onRemoveFilterClicked}}
            @onSetPage={{@controller.onSetPage}}
            @pageSize={{or
              @controller.mappingPaginationData.pageSize
              DEFAULT_PAGE_SIZE
            }}
            @propertyOptions={{@controller.propertyOptions}}
            @selectedMappingEntries={{@controller.selectedMappingEntries}}
            @selectedRowIds={{@controller.selectedRowIds}}
            @setFilterOption={{@controller.setFilterOption}}
            @setMarketMappingFilters={{@controller.setMarketMappingFilters}}
            @setSelectedMappingEntries={{@controller.setSelectedMappingEntries}}
            @setSelectedRowIds={{@controller.setSelectedRowIds}}
            @setValueType={{@controller.setValueType}}
            @startIndexPage={{or
              @controller.mappingPaginationData.startIndex
              0
            }}
            @totalFilteredProperties={{@controller.totalFilteredProperties}}
            @totalNoOfItems={{or
              @controller.mappingPaginationData.totalNoOfItems
              0
            }}
            @valueType={{@controller.valueType}}
          />
        </div>
      </div>
    </:default>
  </SettingsPageTemplate>

  {{#if @controller.showUserOnboardingModal}}
    <SettingsCentralMappingModalsModalMappingOnboarding
      @onClose={{@controller.closeUserOnboardingModal}}
    />
  {{/if}}

  {{#if @controller.isShowingUploadModal}}
    <SettingsCentralMappingModalsModalUploadMapping
      @isUploadRunning={{@controller.uploadMappingTask.isRunning}}
      @onClose={{@controller.onCloseUploadModal}}
      @onUpload={{@controller.handleUploadFile}}
    />
  {{/if}}

  {{#if @controller.isShowingSegmentationViewModal}}
    {{#if @controller.organizationSettings}}
      <SettingsMappingModalsSegmentationViewModal
        @onCloseModal={{@controller.onCloseSegmentationViewModal}}
        @onSaved={{@controller.onSegmentationViewSaved}}
        @settings={{@controller.organizationSettings}}
      />
    {{/if}}
  {{/if}}
</template> satisfies TOC<MarketSettingsCentralMappingMappingSignature>;
